import type { HTMLAttributes } from 'react';
import { useEffect, useState } from 'react';

import { Button, Dialog, RadixDialogPrimitives } from '@capsule/consumer-design-system';

import { useHistory } from 'react-router';
import { css } from 'styled-components';

import {
  SimpleFeatureLayout,
  SimpleFeatureLayoutPageContainer,
} from 'src/app/components/FeatureLayouts/SimpleFeatureLayout/SimpleFeatureLayout';
import { MessagesArt } from 'src/app/components/Pages/EscriptOnboarding/steps/SayCapsule';
import { usePOTSExperimentInterstitial } from 'src/app/hooks/feature_data/usePOTSExperiment';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';
import { loggedInTransferPrompt, medications } from 'src/app/router/paths';

const OpenedPillBottle = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 72 96" aria-hidden className={className}>
    <path
      fill="#14294E"
      d="M44.3 45.81C32.16 36.38 27.06 32.94 26.92 33.1v.15c-.15.3 4.34 4.34 16.46 13.77 12.13 9.43 17.22 12.88 17.37 12.73v-.15c.15-.15-4.34-4.34-16.47-13.78Z"
    />
    <path fill="#EF4931" d="M40.7 46.26a4.64 4.64 0 1 0 0-9.28 4.64 4.64 0 0 0 0 9.28Z" />
    <path fill="#E42327" d="M41.15 36.98h-.9c-.3 0-.6.15-.9.15l.9 8.98h.9c.3 0 .6-.15.9-.15l-.9-8.98Z" />
    <path fill="#EF4931" d="M35.46 34.88a4.64 4.64 0 1 0 0-9.28 4.64 4.64 0 0 0 0 9.28Z" />
    <path fill="#E42327" d="M35.9 25.75h-.89c-.3 0-.6.15-.9.15l.75 8.98h.9c.3 0 .6-.15.9-.15l-.75-8.98Z" />
    <path fill="#EF4931" d="M47.44 34.73a4.64 4.64 0 1 0 0-9.28 4.64 4.64 0 0 0 0 9.28Z" />
    <path fill="#E42327" d="M42.95 29.2c0 .3-.15.6-.15.9v.9l8.98.14c0-.3.15-.6.15-.9v-.9l-8.98-.15Z" />
    <path fill="#EF4931" d="M39.5 12.88a4.64 4.64 0 1 0 0-9.29 4.64 4.64 0 0 0 0 9.29Z" />
    <path
      fill="#E42327"
      d="M36.66 11.83c.3.15.45.3.75.45.3.15.6.3.9.3l4.19-8.09c-.3-.15-.45-.3-.75-.45-.3-.15-.6-.3-.9-.3l-4.2 8.09Z"
    />
    <path fill="#EF4931" d="M56.72 9.28a4.64 4.64 0 1 0 0-9.28 4.64 4.64 0 0 0 0 9.28Z" />
    <path fill="#E42327" d="M52.23 3.44c-.15.3-.15.6-.15.9v.9l8.98.6c.15-.3.15-.6.15-.9v-.9l-8.98-.6Z" />
    <path fill="#EF4931" d="M47.29 22.9a4.64 4.64 0 1 0 0-9.28 4.64 4.64 0 0 0 0 9.29Z" />
    <path
      fill="#E42327"
      d="M42.65 18.72c0 .3.15.6.15.9 0 .3.15.6.3.9l8.68-2.7c0-.3-.15-.6-.15-.9-.15-.3-.15-.6-.3-.9l-8.68 2.7Z"
    />
    <path
      fill="#E0ECEB"
      d="M63.16 56.9c.3 0 .75-5.25 1.04-18.72.3-13.48-.14-18.72-.44-18.72l-7.04-.15c-.3 0-.75 5.24-1.05 18.72-.15 13.32.15 18.71.45 18.71l7.04.15Z"
    />
    <path
      fill="#CCDFDD"
      d="M63.16 56.9c-.3 0-.9-5.4-.75-18.72.15-13.33 1.05-18.72 1.35-18.72.3 0 .6 5.4.44 18.72-.15 13.32-.74 18.71-1.04 18.71Z"
    />
    <path
      fill="#E0ECEB"
      d="M70.64 54.5c.3 0 .75-4.64 1.05-16.17.15-11.53-.15-16.17-.45-16.17L64.2 22c-.3 0-.74 4.64-1.04 16.17-.15 11.53.15 16.17.45 16.17l7.03.15Z"
    />
    <path
      fill="#CCDFDD"
      d="M70.64 54.5c-.3 0-.6-4.64-.45-16.17.15-11.53.75-16.17 1.05-16.17.3 0 .6 4.64.45 16.17-.3 11.53-.75 16.17-1.05 16.17Z"
    />
    <path
      fill="#99BFBC"
      d="m61.15 38.97.01-1.04-3.74-.06-.01 1.05 3.74.05ZM61.07 41.06l.02-1.04-3.74-.06-.02 1.04 3.74.06ZM61.17 43.13l.02-1.05-3.74-.06-.02 1.05 3.74.06ZM61.13 45.2l.02-1.05-3.75-.06-.01 1.04 3.74.06ZM61.1 47.23l.03-1.05-3.75-.06-.01 1.05 3.74.06ZM61.1 49.26l.02-1.05-3.75-.06-.01 1.05 3.74.06ZM61.27 51.26l.02-1.05-3.75-.06-.01 1.05 3.74.06ZM61.32 53.38l.02-1.05-3.74-.06-.02 1.05 3.74.06ZM61.42 55.45l.02-1.05-3.75-.06-.01 1.05 3.74.06ZM61.94 22.51l.02-1.04-3.74-.06-.02 1.04 3.74.06ZM61.73 24.59l.02-1.05-3.75-.06-.01 1.05 3.74.06ZM61.48 26.55l.01-1.04-3.74-.06-.02 1.04 3.75.06ZM61.51 28.68l.02-1.04-3.75-.06-.01 1.04 3.74.06ZM61.37 30.7l.02-1.06-3.75-.06-.01 1.05 3.74.06ZM61.26 32.82l.01-1.05-3.74-.06-.02 1.05 3.75.06ZM61.31 34.78l.02-1.05-3.74-.06-.02 1.05 3.74.06ZM61.22 36.89l.02-1.05-3.75-.06-.01 1.05 3.74.06Z"
    />
    <path
      fill="#0F3C6C"
      d="M42.8 47.61c-9.14-7.19-13.03-9.58-13.18-9.43v.15L.28 75.9v.15c-.15.3 2.69 4.04 11.67 11.23 9.14 7.18 13.48 8.98 13.63 8.68v-.15l29.5-37.58v-.15c.3-.15-3.15-3.44-12.28-10.48Z"
    />
    <path
      fill="#0F3C6C"
      d="M60.61 59.74c-.15.3-5.24-3.15-17.37-12.73-12.12-9.43-16.61-13.62-16.46-13.77l-2.7 3.44v.15c-.15.3 4.34 4.34 16.47 13.77 12.13 9.44 17.22 12.88 17.37 12.73v-.15l2.7-3.44Z"
    />
    <path fill="#0F3C6C" d="m2.37 78.75 1.95 1.95 28.44-36.38c-.74-.6-1.5-1.2-2.1-1.65L2.38 78.75Z" opacity=".75" />
    <path
      fill="#14294E"
      d="M40.55 50.6a724.83 724.83 0 0 1-12.58-10.18c-.15 0 0 0 0 0 3.6 3.75 11.98 13.93 23.96 21.86 0 0 1.35-1.8 1.64-1.94-2.99-2.1-7.18-5.24-13.02-9.74Z"
    />
    <path
      fill="#fff"
      d="M39.2 58.39c-5.09-4.04-7.03-5.54-7.18-5.39L14.35 75.6c-.15.16 1.65 2.1 6.74 6.15 5.09 4.04 7.48 5.24 7.48 5.09l17.67-22.61c.15-.3-1.95-1.8-7.04-5.84Z"
    />
    <path fill="#FFD300" d="M30.82 62.28c-.75-.6-1.8-.45-2.4.3-.6.75-.45 1.8.3 2.4l2.25 1.8 2.1-2.85-2.25-1.65Z" />
    <path fill="#99BFBC" d="M35.9 70.67c.76.6 1.8.45 2.4-.3.6-.75.45-1.8-.3-2.4l-2.24-1.8-2.1 2.7 2.25 1.8Z" />
    <path
      fill="#0F3C6C"
      d="M29.47 71.42c.45.3 1.05.44 1.5.3.15 0 .15 0 .3-.15.3-.15.45-.3.75-.45l1.8-2.25 2.09-2.7-4.8.6-1.79 2.25c-.15.3-.3.45-.3.75v.45c-.15.45 0 .9.45 1.2Z"
    />
    <path
      fill="#EF4931"
      d="m35.76 66.18 1.8-2.25c.6-.75.44-1.8-.3-2.4-.75-.6-1.8-.45-2.4.3l-1.8 2.1-2.1 2.7 4.8-.45Z"
    />
    <path
      fill="#0F3C6C"
      d="m23.7 70.74-.75.94 7.9 6.18.74-.94-7.9-6.18ZM22.23 72.4l-.74.95 7.9 6.18.74-.95-7.9-6.18ZM20.89 74.16l-.74.94 7.9 6.18.74-.94-7.9-6.18Z"
    />
  </svg>
);

const CapsuleHeart = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 133 123" aria-hidden className={className}>
    <path
      fill="#fff"
      fill-rule="evenodd"
      d="M104.2.97C81-4 69.61 11.57 67.06 15.67a.66.66 0 0 1-1.12 0C63.39 11.57 52-4 28.8.97c0 0-31.56 8.35-27.75 40.58 0 0 1.49 24.65 28.79 51.19 15.05 14.63 26.25 24.79 32.1 28.81a8.27 8.27 0 0 0 9.25.06c5.66-3.8 16.14-13.48 31.97-28.87 27.3-26.54 28.8-51.19 28.8-51.19C135.75 9.32 104.2.97 104.2.97Z"
      clip-rule="evenodd"
    />
    <path
      fill="#EF4931"
      fill-rule="evenodd"
      d="M41.04 43.45c-1.2 1.2-1.6 3.21.88 5.66l2.88 2.86.02.02 2.88 2.86c2.47 2.45 4.5 2.07 5.7.87 1.21-1.2 1.6-3.2-.87-5.66l-2.88-2.86a4.77 4.77 0 0 1-.02-.02l-2.88-2.86c-2.48-2.45-4.5-2.07-5.71-.87ZM33.7 78.5c-1.24 1.17-1.68 3.15.77 5.62l2.85 2.89.03.02 2.85 2.88c2.45 2.48 4.5 2.14 5.76.98 1.24-1.16 1.68-3.14-.77-5.61l-2.85-2.89-.03-.02-2.85-2.88C37 77 34.96 77.35 33.7 78.5ZM36.99 19.1c-.92.38-1.57 1.37-.75 3.19l.96 2.12v.02l.96 2.12c.82 1.82 2 2.04 2.92 1.65.92-.39 1.57-1.38.75-3.2l-.95-2.12-.01-.02-.95-2.12c-.82-1.82-2.01-2.04-2.93-1.65ZM20.8 38.24c-.49.88-.37 2.07 1.43 3.05a1475.93 1475.93 0 0 1 2.12 1.15c.1.04.53.28 2.1 1.13 1.8.98 2.89.43 3.37-.45.48-.87.36-2.07-1.44-3.04l-2.1-1.14-.02-.01-2.1-1.14c-1.8-.97-2.89-.42-3.37.45ZM24.3 69.75c-.7.67-.97 1.83.39 3.3a2025.75 2025.75 0 0 1 3.16 3.46c1.35 1.47 2.5 1.29 3.21.62.71-.68.97-1.84-.38-3.31a3288.4 3288.4 0 0 1-3.17-3.45c-1.35-1.48-2.5-1.3-3.21-.62ZM65.9 74.92c-1.04 0-2.1.7-2.09 2.8l.01 2.43v2.46c.01 2.1 1.07 2.8 2.12 2.79 1.05 0 2.1-.7 2.1-2.8l-.01-2.44V77.7c-.01-2.1-1.07-2.79-2.12-2.78ZM32.7 24.37l-4.14-4.11c-3.96-3.92-7.2-3.31-9.13-1.4-1.93 1.92-2.55 5.14 1.4 9.07l4.14 4.1 7.73-7.66ZM26.04 33.15l4.15 4.11c3.95 3.92 7.2 3.31 9.13 1.4 1.93-1.92 2.55-5.14-1.4-9.07l-4.15-4.1-7.73 7.66ZM26.9 47.23h-6.06c-5.76 0-7.68 2.75-7.68 5.5s1.92 5.5 7.68 5.5h6.05v-11ZM27.95 58.23h6.04c5.77 0 7.69-2.75 7.69-5.5s-1.92-5.5-7.69-5.5h-6.04v11ZM56.1 27.85v-5.9c0-5.6-2.8-7.47-5.6-7.47s-5.61 1.86-5.61 7.48v5.89H56.1Z"
      clip-rule="evenodd"
    />
    <path
      fill="#EF4931"
      fill-rule="evenodd"
      d="M44.89 28.89v5.88c0 5.61 2.8 7.48 5.6 7.48s5.61-1.87 5.61-7.48V28.9H44.9Zm-3.87 14.28c-1.18 1.18-1.56 3.15.86 5.56l2.81 2.8.03.03 2.81 2.8c2.42 2.41 4.4 2.04 5.58.86 1.18-1.18 1.56-3.15-.85-5.56-2.1-2.1-2.7-2.69-2.82-2.8l-.02-.03c-.12-.12-.72-.7-2.82-2.8-2.42-2.41-4.4-2.03-5.58-.86ZM48.54 81.93l2.53 5.24c2.4 4.99 5.71 5.5 8.23 4.36 2.51-1.14 4.22-3.94 1.82-8.93l-2.52-5.23-10.06 4.56ZM58.05 75.69l-2.52-5.24c-2.4-5-5.72-5.5-8.23-4.37-2.52 1.14-4.23 3.94-1.83 8.94L48 80.25l10.05-4.56ZM62.28 102.53a3.95 3.95 0 0 1-3.97 3.93 3.95 3.95 0 0 1-3.96-3.93 3.95 3.95 0 0 1 3.96-3.94 3.95 3.95 0 0 1 3.97 3.94ZM54.35 95.71a3.95 3.95 0 0 1-3.96 3.94 3.95 3.95 0 0 1-3.96-3.94 3.95 3.95 0 0 1 3.96-3.93 3.95 3.95 0 0 1 3.96 3.93ZM26.9 64.79a3.95 3.95 0 0 1-3.97 3.93 3.95 3.95 0 0 1-3.96-3.93 3.95 3.95 0 0 1 3.96-3.93 3.95 3.95 0 0 1 3.96 3.93ZM43.8 12.9a3.95 3.95 0 0 1-3.97 3.93 3.95 3.95 0 0 1-3.96-3.93 3.95 3.95 0 0 1 3.96-3.93 3.95 3.95 0 0 1 3.96 3.93ZM33.23 14.48a3.95 3.95 0 0 1-3.96 3.93 3.95 3.95 0 0 1-3.96-3.93 3.95 3.95 0 0 1 3.96-3.93 3.95 3.95 0 0 1 3.96 3.93ZM63.86 44.87a3.95 3.95 0 0 1-3.96 3.93 3.95 3.95 0 0 1-3.96-3.93 3.95 3.95 0 0 1 3.96-3.93 3.95 3.95 0 0 1 3.96 3.93ZM50.13 61.12a3.95 3.95 0 0 1-3.96 3.93 3.95 3.95 0 0 1-3.97-3.93 3.95 3.95 0 0 1 3.97-3.93 3.95 3.95 0 0 1 3.96 3.93ZM18.97 42.26A3.95 3.95 0 0 1 15 46.19a3.95 3.95 0 0 1-3.96-3.93A3.95 3.95 0 0 1 15 38.33a3.95 3.95 0 0 1 3.96 3.93ZM19.5 32.3a3.95 3.95 0 0 1-3.96 3.93 3.95 3.95 0 0 1-3.97-3.93 3.95 3.95 0 0 1 3.97-3.93 3.95 3.95 0 0 1 3.96 3.93ZM67.03 38.34a6.16 6.16 0 0 0 5.59-6.04c0-3.13-2.45-5.7-5.6-6.04v12.08ZM65.45 26.26a6.16 6.16 0 0 0-5.6 6.04c0 3.13 2.45 5.7 5.6 6.04V26.26ZM40.62 65.05a6.04 6.04 0 0 0-6.07-5.24 6.04 6.04 0 0 0-6.08 5.24h12.15ZM28.47 67.14a6.04 6.04 0 0 0 6.08 5.25c3.14 0 5.73-2.3 6.07-5.25H28.47ZM90.9 43.45c1.21 1.2 1.6 3.21-.87 5.66l-2.88 2.86-.03.02-2.88 2.86c-2.47 2.45-4.5 2.07-5.7.87-1.21-1.2-1.6-3.2.87-5.66l2.88-2.86.03-.02 2.88-2.86c2.47-2.45 4.5-2.07 5.7-.87ZM98.77 78.5c1.24 1.17 1.68 3.15-.77 5.62l-2.85 2.89-.03.02-2.85 2.88c-2.45 2.48-4.5 2.14-5.75.98-1.25-1.16-1.69-3.14.76-5.61l2.85-2.89.03-.02L93 79.49c2.45-2.48 4.51-2.14 5.76-.98ZM95.48 19.1c.92.38 1.56 1.37.75 3.19l-.96 2.12v.02l-.96 2.12c-.82 1.82-2 2.04-2.92 1.65-.92-.39-1.57-1.38-.75-3.2a3433.04 3433.04 0 0 0 .96-2.14l.95-2.12c.82-1.82 2.01-2.04 2.93-1.65ZM111.15 38.24c.48.88.36 2.07-1.44 3.05l-2.1 1.14h-.02c-.09.05-.53.3-2.1 1.14-1.8.98-2.88.43-3.36-.45-.48-.87-.37-2.07 1.44-3.04 1.57-.85 2-1.1 2.1-1.14l.02-.01 2.1-1.14c1.8-.97 2.88-.42 3.36.45ZM107.11 69.75c.71.67.97 1.83-.38 3.3l-1.58 1.72-.01.02-1.58 1.72c-1.35 1.47-2.5 1.29-3.21.62-.7-.68-.97-1.84.39-3.31l1.57-1.72.02-.02 1.57-1.71c1.35-1.48 2.5-1.3 3.21-.62ZM99.24 24.37l4.15-4.11c3.95-3.92 7.2-3.31 9.13-1.4 1.93 1.92 2.55 5.14-1.4 9.07l-4.15 4.1-7.73-7.66ZM105.8 33.18l-4.15 4.11c-3.95 3.92-7.2 3.3-9.13 1.4-1.93-1.92-2.55-5.15 1.4-9.07l4.15-4.11 7.72 7.67ZM105.58 47.23h6.05c5.77 0 7.68 2.75 7.68 5.5s-1.91 5.5-7.68 5.5h-6.05v-11ZM104.52 58.23h-6.04c-5.77 0-7.69-2.75-7.69-5.5s1.92-5.5 7.69-5.5h6.04v11ZM76.53 27.84v-5.88c0-5.62 2.8-7.48 5.61-7.48 2.8 0 5.61 1.86 5.61 7.48v5.88H76.53ZM87.79 28.87v5.88c0 5.6-2.8 7.47-5.61 7.47-2.8 0-5.61-1.87-5.61-7.47v-5.88h11.22ZM83.4 81.93l-2.52 5.24c-2.4 4.99-5.72 5.5-8.23 4.36-2.51-1.14-4.23-3.94-1.82-8.93l2.52-5.23 10.05 4.56ZM73.9 75.69l2.52-5.24c2.4-5 5.71-5.5 8.23-4.37 2.51 1.14 4.22 3.94 1.82 8.94l-2.52 5.23-10.06-4.56ZM70.72 102.53a3.95 3.95 0 0 0 3.97 3.93 3.95 3.95 0 0 0 3.96-3.93 3.95 3.95 0 0 0-3.96-3.94 3.95 3.95 0 0 0-3.97 3.94ZM62.27 108.82a3.95 3.95 0 0 0 3.97 3.93 3.95 3.95 0 0 0 3.96-3.93 3.95 3.95 0 0 0-3.96-3.93 3.95 3.95 0 0 0-3.97 3.93ZM62.27 95.45a3.95 3.95 0 0 0 3.97 3.93 3.95 3.95 0 0 0 3.96-3.93 3.95 3.95 0 0 0-3.96-3.93 3.95 3.95 0 0 0-3.97 3.93ZM78.12 95.71a3.95 3.95 0 0 0 3.96 3.94 3.95 3.95 0 0 0 3.96-3.94 3.95 3.95 0 0 0-3.96-3.93 3.95 3.95 0 0 0-3.96 3.93ZM105.58 64.79a3.95 3.95 0 0 0 3.96 3.93 3.95 3.95 0 0 0 3.96-3.93 3.95 3.95 0 0 0-3.96-3.93 3.95 3.95 0 0 0-3.96 3.93ZM88.68 12.9a3.95 3.95 0 0 0 3.96 3.93 3.95 3.95 0 0 0 3.96-3.93 3.95 3.95 0 0 0-3.96-3.93 3.95 3.95 0 0 0-3.96 3.93ZM99.24 14.48a3.95 3.95 0 0 0 3.96 3.93 3.95 3.95 0 0 0 3.97-3.93 3.95 3.95 0 0 0-3.97-3.93 3.95 3.95 0 0 0-3.96 3.93ZM68.61 44.87a3.95 3.95 0 0 0 3.96 3.93 3.95 3.95 0 0 0 3.96-3.93 3.95 3.95 0 0 0-3.96-3.93 3.95 3.95 0 0 0-3.96 3.93ZM82.87 61.12a3.95 3.95 0 0 0 3.96 3.93 3.95 3.95 0 0 0 3.96-3.93 3.95 3.95 0 0 0-3.96-3.93 3.95 3.95 0 0 0-3.96 3.93ZM114.03 42.26a3.95 3.95 0 0 0 3.96 3.93 3.95 3.95 0 0 0 3.96-3.93 3.95 3.95 0 0 0-3.96-3.93 3.95 3.95 0 0 0-3.96 3.93ZM112.97 32.3a3.95 3.95 0 0 0 3.97 3.93 3.95 3.95 0 0 0 3.96-3.93 3.95 3.95 0 0 0-3.97-3.94 3.95 3.95 0 0 0-3.96 3.94ZM91.32 65.05a6.04 6.04 0 0 1 6.07-5.24c3.15 0 5.74 2.3 6.08 5.24H91.32ZM103.47 67.14a6.04 6.04 0 0 1-6.08 5.25 6.04 6.04 0 0 1-6.07-5.25h12.15Z"
      clip-rule="evenodd"
    />
    <path
      fill="#EF4931"
      d="M65.63 72.75c-1.99 0-3.6-1.6-3.6-3.57V66.1l8.88-8.83h3.1c1.98 0 3.59 1.6 3.59 3.57 0 1.97-1.6 3.57-3.6 3.57h-4.78v4.77c0 1.97-1.6 3.57-3.6 3.57ZM69.22 55.58l-8.88 8.83h-3.1c-1.98 0-3.59-1.6-3.59-3.57 0-1.97 1.61-3.57 3.6-3.57h4.79V52.5c0-1.97 1.6-3.57 3.59-3.57 1.98 0 3.59 1.6 3.59 3.57v3.08Z"
    />
  </svg>
);

const CircleCheckmark = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    width={24}
    height={24}
    aria-hidden
    className={className}
  >
    <circle xmlns="http://www.w3.org/2000/svg" fill="#fff" cx="12" cy="12" r="10" />
    <path stroke="#0F3C6C" stroke-linecap="round" stroke-width="2" d="M7.5 12.38 10.24 15l6.26-6" />
  </svg>
);

const WhiteCircleCheckmark = () => <CircleCheckmark />;

const GrayCircleCheckmark = () => (
  <CircleCheckmark
    css={css`
      circle {
        fill: ${({ theme }) => theme.colors.capsuleGray50};
      }
      path {
        stroke: ${({ theme }) => theme.colors.white};
      }
    `}
  />
);

const AlertMark = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" width={24} height={24} aria-hidden>
    <path
      fill="#0F3C6C"
      d="M13.75 16.75a1.25 1.25 0 1 1-2.5 0 1.25 1.25 0 0 1 2.5 0ZM12.5 6a1.4 1.4 0 0 0-1.4 1.5l.34 5.5a1.06 1.06 0 0 0 2.12 0l.35-5.5A1.4 1.4 0 0 0 12.5 6Z"
    />
    <path
      fill="#0F3C6C"
      fill-rule="evenodd"
      d="M12.5 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20Zm0-2a8 8 0 1 0 0-16 8 8 0 0 0 0 16Z"
      clip-rule="evenodd"
    />
  </svg>
);

const UnorderedList = ({ children, className }: HTMLAttributes<HTMLUListElement>) => (
  <ul
    css={css`
      display: flex;
      flex-direction: column;
      gap: ${({ theme }) => theme.space.s2};
      margin: 0 auto;
      padding: 0;
      list-style: none;
      height: 100%;
      width: auto;

      & > li {
        display: flex;
        align-items: center;
        gap: ${({ theme }) => theme.space.s2};
      }
    `}
    className={className}
  >
    {children}
  </ul>
);

export const PostOrderNextSessionInterstitial = () => {
  const { variant, isLoading } = usePOTSExperimentInterstitial();
  const dispatchViewMCYPInterstitial = useSegmentEvent('viewMCYPInterstitial', {
    shouldTrackOnMount: false,
  });
  const [hasViewEventFired, setHasViewEventFired] = useState(false);

  useEffect(() => {
    if (!hasViewEventFired && variant?.includes('interstitial_') && !isLoading) {
      const splitVariant = variant.split('interstitial_');
      if (splitVariant?.length > 1) {
        dispatchViewMCYPInterstitial({
          variant: variant!.split('interstitial_')[1], // interstitial_bring_all_meds --> bring_all_meds
        });
        setHasViewEventFired(true);
      }
    }
  }, [dispatchViewMCYPInterstitial, hasViewEventFired, isLoading, variant]);

  const { push } = useHistory();

  const trackPositiveCTAClick = useSegmentEvent('selectTransferMedsPOTS', { shouldTrackOnMount: false });
  const trackNegativeCTAClick = useSegmentEvent('selectDoNotTransferMedicationPOTS', { shouldTrackOnMount: false });

  const isVariantA = variant === 'interstitial_complete_checklist';
  const isVariantB = variant === 'interstitial_bring_all_meds';

  // 🤷🏽‍♂️ No loading state given
  if (isLoading) return null;

  if (!variant || variant === 'control' || variant === 'off') {
    push(medications.route);
    return;
  }

  const goHome = () => push(medications.route);

  console.log('PostOrderNextSessionInterstitial:', { variant, isVariantA, isVariantB });
  return (
    <SimpleFeatureLayoutPageContainer key="pots-experiment-post-order-interstial">
      <SimpleFeatureLayout
        keyName="pots-experiment-post-order-interstial"
        backButton={{ isRendered: false }}
        closeButtonClickHandler={() => {
          trackNegativeCTAClick();
          goHome();
        }}
        title={
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: ${({ theme }) => theme.space.s5};
              align-items: center;
              text-align: center;
            `}
          >
            {isVariantA && (
              <OpenedPillBottle
                css={css`
                  height: 9.6rem;
                  width: auto;
                `}
              />
            )}

            {isVariantB && (
              <CapsuleHeart
                css={css`
                  height: auto;
                  width: 12rem;
                `}
              />
            )}

            <h2
              css={css`
                all: unset;
              `}
            >
              {isVariantA && `Let’s bring all your meds to Capsule`}
              {isVariantB && `Complete your checklist`}
            </h2>
          </div>
        }
        description={
          isVariantA ? (
            <span
              css={css`
                display: block;
                text-align: center;
                margin-top: ${({ theme }) => theme.space.s3};
                margin-left: auto;
                margin-right: auto;
              `}
            >
              It only takes 15 seconds.
            </span>
          ) : undefined
        }
        body={{
          content: (
            <UnorderedList
              css={
                isVariantB
                  ? css`
                      &&& {
                        gap: ${({ theme }) => theme.space.s4};
                      }
                    `
                  : undefined
              }
            >
              {isVariantA && (
                <>
                  <li>
                    <WhiteCircleCheckmark /> We contact your pharmacy or doctor
                  </li>
                  <li>
                    <WhiteCircleCheckmark /> Insurance accepted, same co-pay
                  </li>
                  <li>
                    <WhiteCircleCheckmark /> Free delivery
                  </li>
                </>
              )}

              {isVariantB && (
                <>
                  <li
                    css={css`
                      color: ${({ theme }) => theme.colors.capsuleGray50};
                    `}
                  >
                    <GrayCircleCheckmark /> Add allergy information
                  </li>
                  <li
                    css={css`
                      color: ${({ theme }) => theme.colors.capsuleGray50};
                    `}
                  >
                    <GrayCircleCheckmark /> List other med types
                  </li>

                  <li>
                    <AlertMark /> Move your other medications
                  </li>
                </>
              )}
            </UnorderedList>
          ),
          type: 'artwork',
        }}
        cta1={{
          children: isVariantA ? 'Bring my other meds' : 'Continue',
          onClick: () => {
            trackPositiveCTAClick();
            push(loggedInTransferPrompt.route);
          },
        }}
        cta2={
          <Dialog
            title="Sure, use Capsule anytime"
            description="If you want to get your other meds delivered for free, just tell your doctor “Capsule.”"
            triggerButton={{
              children: 'Not Right Now',
              onClick: () => trackNegativeCTAClick(),
              variant: 'text-md',
              isFullWidth: true,
            }}
          >
            <MessagesArt />

            <RadixDialogPrimitives.Close asChild>
              <Button variant="contained-secondary-lg" isFullWidth onClick={goHome}>
                Done
              </Button>
            </RadixDialogPrimitives.Close>
          </Dialog>
        }
      />
    </SimpleFeatureLayoutPageContainer>
  );
};
